import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiKey, ApiKeyEntryProps, ApiKeyType } from '@ixicare/resources';
import { t } from '~i18n';
import { useStores } from '~store/utils';

const queryKey = (siteUuid?: string) => ['site-api-keys', siteUuid];

export const useSiteApiKeys = (siteUuid?: string) => {
  return useQuery<ApiKeyEntryProps[]>({
    queryKey: queryKey(siteUuid),
    queryFn: () => ApiKey.getSiteApiKeys(siteUuid!),
    enabled: !!siteUuid,
  });
};

export const useSiteApiKeyMutations = (siteUuid: string) => {
  const queryClient = useQueryClient();
  const { notifications } = useStores();

  const notify = (notificationType: 'success' | 'error', key: string) =>
    notifications.addUserActionNotification({ notificationType, message: t(key) });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKey(siteUuid) });

  // Errors stay on the mutation so the create form can show them next to the field they concern.
  const create = useMutation({
    mutationFn: (input: { name: string; keyType: ApiKeyType; expiresAt: Date | null }) =>
      ApiKey.createSiteApiKey(siteUuid, input),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (prefix: string) => ApiKey.deleteApiKey(prefix),
    onSuccess: () => {
      invalidate();
      notify('success', 'site.developer.keys.deleteSuccess');
    },
    onError: () => notify('error', 'site.developer.keys.deleteError'),
  });

  return { create, remove };
};
